import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, type OnDestroy, type OnInit, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterOutlet } from '@angular/router';
import { PublicFooter } from '@app/components/public-footer/public-footer';
import { getAgeInYears } from '@app/domain/helpers/analytics.helpers';
import { REGISTRATION_FORM_KEY, SHOW_PUBLIC_BANNERS } from '@app/domain/properties';
import { AmplitudeService } from '@app/services/amplitude/amplitude';
import { AppsflyerService } from '@app/services/appsflyer/appsflyer';
import { GtmService } from '@app/services/gtm/gtm';
import { OneSignalService } from '@app/services/one-signal/one-signal';
import { StorageService } from '@app/services/storage/storage';
import {
  ABORT_STAGE_NAMES,
  ABORT_STAGE_UNKNOWN,
  FIRST_STEP,
} from '@app/views/sign-up/domain/properties/sign-up-form.properties';
import { RegistrationFormService } from '@app/views/sign-up/services/registration-form/registration-form.service';
import { WelcomeSlider } from '@app/views/welcome/components/welcome-slider/welcome-slider';

@Component({
  selector: 'bingo-sign-up',
  imports: [
    FormsModule,
    NgOptimizedImage,
    PublicFooter,
    RouterOutlet,
    WelcomeSlider,
  ],
  templateUrl: './sign-up.view.html',
  styleUrl: './sign-up.view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpView implements OnDestroy, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly sessionStorage = inject(StorageService);
  private readonly gtmService = inject(GtmService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly appsflyerService = inject(AppsflyerService);
  private readonly oneSignalService = inject(OneSignalService);
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly showBanners = SHOW_PUBLIC_BANNERS;
  readonly registrationService = inject(RegistrationFormService);

  ngOnInit(): void {
    this.registrationService.resetCompleted();
    this.registrationService.formChangesWatcher(this.destroyRef);
  }

  ngOnDestroy(): void {
    this.trackAbort();
    this.sessionStorage.removeItem(REGISTRATION_FORM_KEY);
  }

  /**
   * The wizard shell is destroyed on any way out — Back from step 1, a route change, or a
   * successful registration; only the last of those sets the completed flag. Every analytics sink
   * reports abandonment from here, with one stage vocabulary, so their funnels line up. Closing
   * the tab does not destroy the view, so it is not reported.
   */
  private trackAbort(): void {
    if (this.registrationService.isCompleted() || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const stageIndex = this.registrationService.currentStep() - FIRST_STEP;
    const stageName = ABORT_STAGE_NAMES[stageIndex] ?? ABORT_STAGE_UNKNOWN;

    this.gtmService.pushSignUpAbort(stageName);
    this.appsflyerService.sendLogEvent('af_sign_up_abort', { stage_name: stageName });
    this.oneSignalService.sendTrigger('signup_incomplete', 'true');
    this.trackAmplitudeAbort(stageName);
  }

  private trackAmplitudeAbort(stageName: string): void {
    const personal = this.registrationService.form.value.personal;

    this.amplitudeService.trackEvent('sign_up_abort', {
      aborted_stage: stageName,
      age: getAgeInYears(personal?.birthDate),
      first_name: personal?.firstName,
      last_name: personal?.lastName,
    });
  }
}
